// schema.org pieces shared by the React pages (SEOHead, StoreStructuredData).
// The backend crawler pages (backend/utils/seo.py) build the same data for
// bots that do not run JavaScript: keep the two in step.

import { DEFAULT_RETURN_WINDOW_DAYS } from './storeDetails';

export const SITE_URL = 'https://anum360.ae';
export const STORE_ID = `${SITE_URL}/#store`;

// Admin → Settings `return_fees` → schema.org; nothing when the owner has not said.
export const RETURN_FEES_LD = {
  free: 'https://schema.org/FreeReturn',
  customer: 'https://schema.org/ReturnFeesCustomerResponsibility',
};

/**
 * MerchantReturnPolicy from the real facts (GET /settings/public/store): the
 * return window POST /returns enforces and, only when the owner has stated
 * it, who pays for the return. Same as backend utils/site_profile.return_policy_ld.
 */
export const returnPolicyLd = ({ days, fees } = {}) => ({
  '@type': 'MerchantReturnPolicy',
  applicableCountry: 'AE',
  returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
  merchantReturnDays: Number.isInteger(days) && days > 0 ? days : DEFAULT_RETURN_WINDOW_DAYS,
  returnMethod: 'https://schema.org/ReturnByMail',
  merchantReturnLink: `${SITE_URL}/returns`,
  ...(RETURN_FEES_LD[fees] ? { returnFees: RETURN_FEES_LD[fees] } : {}),
});

// The policy before the store settings have loaded (window only).
export const RETURN_POLICY_LD = returnPolicyLd();

// Zones that are not domestic UAE delivery (backend utils/merchant_feed.NON_UAE_ZONES).
const NON_UAE_ZONES = new Set(['international', 'gcc']);
const zoneKey = (name) => String(name || '').toLowerCase().replace(/[\s_-]+/g, '');

/**
 * OfferShippingDetails for one item at `price` (AED excl. VAT) from the real
 * delivery-zone table (`normalizeZones` output): the most expensive UAE zone's
 * fee, 0 once the item reaches that zone's free threshold (judged ex-VAT,
 * like an order), plus the zones' delivery days. With `withVatFn` the fee is
 * VAT-inclusive, like the offer price (and backend utils/seo). null when
 * there are no domestic zones (never a guessed fee).
 */
export const offerShippingDetails = (zones, price, withVatFn = (x) => x) => {
  const domestic = (zones || []).filter((z) => z && z.zone && !NON_UAE_ZONES.has(zoneKey(z.zone)));
  if (!domestic.length || !Number.isFinite(Number(price))) return null;
  const amount = Number(price);
  const fee = Math.max(...domestic.map((z) => {
    const base = Math.max(0, Number(z.fee) || 0);
    const threshold = z.freeThreshold;
    return threshold != null && threshold > 0 && amount >= threshold ? 0 : base;
  }));
  const days = domestic.map((z) => z.estimatedDays).filter((d) => Number.isFinite(d) && d > 0);
  return {
    '@type': 'OfferShippingDetails',
    shippingRate: { '@type': 'MonetaryAmount', value: Number(withVatFn(fee)).toFixed(2), currency: 'AED' },
    shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'AE' },
    ...(days.length ? {
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' },
        transitTime: { '@type': 'QuantitativeValue', minValue: Math.min(...days), maxValue: Math.max(...days), unitCode: 'DAY' },
      },
    } : {}),
  };
};

/**
 * The boutique as a schema.org Store, from the owner's Admin → Settings store
 * fields (GET /settings/public/store via normalizeStoreDetails). Only what is
 * set is included; opening hours only once the owner has confirmed them
 * (config/storeHours.js). Same @id as the static OnlineStore in index.html,
 * so search engines merge the two instead of seeing a second business.
 */
export const storeJsonLd = (details, hours) => {
  const d = details || {};
  const data = {
    '@context': 'https://schema.org',
    '@type': ['OnlineStore', 'Store'],
    '@id': STORE_ID,
    name: d.name || 'ANUM 360 GALLERY',
    url: `${SITE_URL}/`,
  };
  if (d.phone) data.telephone = d.phone;
  if (d.email) data.email = d.email;
  if (d.address) {
    data.address = { '@type': 'PostalAddress', streetAddress: d.address, addressCountry: 'AE' };
  }
  if (d.trn) data.vatID = d.trn;
  if (d.foundedYear) data.foundingDate = String(d.foundedYear);
  if (d.city) data.foundingLocation = { '@type': 'Place', name: `${d.city}, United Arab Emirates` };
  if (d.sameAs && d.sameAs.length) data.sameAs = d.sameAs;
  if (hours && hours.confirmed && Array.isArray(hours.schema) && hours.schema.length) {
    data.openingHoursSpecification = hours.schema.map((h) => ({ '@type': 'OpeningHoursSpecification', ...h }));
  }
  data.hasMerchantReturnPolicy = returnPolicyLd({ days: d.returnWindowDays, fees: d.returnFees });
  return data;
};
